import { Injectable } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import { showCoachAlreadyLinkedAlertAsync, showOtherAccountAlertAsync, showStoreRefusedAlertAsync } from 'pages/subscribe/other-account-alert';
import { AuthRole } from '@cf-shared/couch-auth';
import { isCapacitorNative, getCapacitorPlatform } from 'environments/environment-helpers';
import { isPausedPaddleSubscriber } from '@cf-shared/data-model/couch-user';
import { coachSetupConflictsWithMembership, describeCoachCrossChannelBlock, CoachCrossChannelBlockInfo, licenseTypeToBillingProvider, SubscriptionBillingProvider } from '@cf-shared/data-model/subscription-channel-rules';
import { AlertButton, AlertController, AlertButtonRole } from 'providers/alert-controller';
import { PopupMenuProvider, PopupMenuOptions } from 'providers/popup-menu/popup-menu';
import { LoadingController } from 'providers/loading-controller';
import { ToastController } from 'providers/toast-controller';
import { NavController } from 'providers/nav-controller';
import { presentSupportContactAsync } from 'utils/support-contact';
import { ICoachCheckoutPage } from 'pages/coach-checkout/coach-checkout-page-interface';
import { AuthService } from './auth.service';
import { LoggingService } from './logging.service';
import { CoachTierService, CoachTierOption } from './coach-tier.service';
import { CoachTierOutcomeMessage, decideCoachTierOutcomeMessage } from './coach-tier-outcome';
import { ConfigDbService } from './config-db.service';
import { ConfigKey } from './config-keys';

/**
 * The "Become a Coach" onboarding flow (R6.1) — deciding whether to offer it,
 * and running it once the user picks it.
 *
 * It lived in `user-profile.ts` until the page class was split up
 * (`df-5e7f8dd8a6f4`). It sits here rather than on the page because it shares no
 * state with the profile screen at all: it reads the session and calls services,
 * and writes nothing back onto the page. The page keeps only the three-dot menu
 * that enters it.
 *
 * It is NOT folded into `CoachTierService`, which is the data-and-store half of
 * the same feature (catalog, prices, purchase). This service is the dialog and
 * sequencing half, and it depends on that one.
 */
@Injectable({ providedIn: 'root' })
export class BecomeCoachFlowService {
  constructor(
    private auth: AuthService,
    private alertCtrl: AlertController,
    private popupCtrl: PopupMenuProvider,
    private loadingCtrl: LoadingController,
    private toastCtrl: ToastController,
    private navCtrl: NavController,
    private logging: LoggingService,
    private coachTierService: CoachTierService,
    private configDb: ConfigDbService,
  ) {}

  /**
   * Whether to show the "Become a Coach" entry on this platform.
   *  - Remote master switch (`ConfigKey.BecomeCoach`) off → false: coach
   *    onboarding is disabled remotely. Defaults off when the key is unset,
   *    so the entry is hidden until onboarding is turned on in the config DB.
   *  - Already a coach (effective slots > 0, legacy or an active app-store
   *    tier) → false: never re-offer the entry to an existing coach.
   *  - Web → true: the Paddle per-seat coach checkout is always available.
   *  - A mobile store → only when that store is configured for coach
   *    tiers in the catalog (so iOS stays hidden until its products are
   *    created, while Google Play shows once configured).
   */
  async isAvailableAsync(): Promise<boolean> {
    // remote master switch — coach onboarding must be enabled in config (defaults
    // off when the key is absent), otherwise never offer the entry on any platform
    if (!(await this.configDb.getConfigValueAsync<boolean>(ConfigKey.BecomeCoach))) return false;
    // already a coach (legacy slots or an active app-store tier) → don't re-offer
    if (this.auth.getIsCoach()) return false;
    if (!isCapacitorNative()) return true;
    return this.coachTierService.isTierConfiguredForPlatformAsync();
  }

  /**
   * "Become a Coach" entry (R6.1). Store platforms present the fixed-tier
   * picker and run the in-app purchase; the web goes to the Paddle per-seat
   * coach checkout.
   *
   * Cross-channel guard: coaching must bill on the same channel as the
   * user's personal membership (a store coach tier folds in only a
   * same-store membership; web coaching coexists only with a membership on
   * the same web provider). When the membership is on a different channel we
   * block the purchase and explain what to do instead. The copy differs by
   * channel — see `crossChannelBlockMessage`.
   *
   * One of those cases cannot be resolved by the user alone: a membership
   * still billed by our previous web payment provider has to be MOVED to the
   * current one, which is something we do for them. That case therefore also
   * offers a Contact Support button. Every other case describes a self-serve
   * path, so it keeps the plain OK button.
   *
   * `isCallerGone` is the caller's own "has my view been destroyed?" check. This
   * service has no view lifecycle of its own, and the screen that started the
   * flow can be closed while the block alert is still up — so the caller supplies
   * it and the support dialog is skipped when it answers true. It stood as a
   * `this.viewDestroyed` guard while this flow lived on the profile page.
   */
  async startAsync(isCallerGone: () => boolean): Promise<void> {
    const personalProvider = this.activePersonalProvider();
    const conflict = coachSetupConflictsWithMembership(personalProvider, this.currentCoachChannel());

    // block a cross-channel setup with channel-appropriate copy.
    if (conflict) {
      // decide the case ONCE — the message and the button set are two views of
      // the same decision, so deriving them separately could let them disagree
      const blockInfo = describeCoachCrossChannelBlock(personalProvider, isCapacitorNative());
      const offerSupport = blockInfo.kind === 'legacyWeb';
      // When we offer support, OK takes the Cancel role and Contact Support
      // takes Primary. Two reasons: reaching us IS the next step this message
      // asks for, and a backdrop tap / browser Back maps to the Cancel-role
      // button — so putting OK there makes dismissing the alert do nothing,
      // instead of silently opening the support dialog.
      const buttons: AlertButton<boolean>[] = offerSupport
        ? [
            { text: LL.common.buttons.ok(), role: AlertButtonRole.Cancel, result: false },
            { text: LL.profile.contactSupport.title(), role: AlertButtonRole.Primary, result: true },
          ]
        : [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: false }];
      const wantsSupport = await this.alertCtrl.showAlertAsync({
        title: LL.coach.tier.becomeCoach(),
        message: this.crossChannelBlockMessage(blockInfo),
        buttons,
      });
      if (isCallerGone()) return;
      // open the support dialog only after this alert has closed — nesting one
      // dialog inside another's handler is what the support helper avoids
      if (wantsSupport) {
        await presentSupportContactAsync(this.alertCtrl, this.toastCtrl, this.auth.user.email);
      }
      return;
    }

    if (isCapacitorNative()) {
      await this.presentCoachTierPickerAsync();
    } else {
      // web: the Paddle per-seat rail (Phase 4, #3919). Navigate to the
      // dedicated coach-checkout page, which hosts the EMBEDDED inline Paddle
      // checkout (parity with the subscribe page — not a pop-up overlay). That
      // page runs the authoritative cross-provider gate again as a backstop for
      // the web-to-web PayPro case the client pre-check above cannot fully see.
      await this.navCtrl.pushAsync(ICoachCheckoutPage, {});
    }
  }

  /**
   * Turn a cross-channel block case into the words we show. Which CASE applies
   * is decided by the shared `describeCoachCrossChannelBlock` (so it can be
   * unit-tested away from Angular); this method only maps kind to copy.
   *
   * Every kind is listed explicitly and there is no `default` — that is what
   * makes the compiler reject a newly added kind here instead of letting it
   * fall through to the generic wording, which is exactly the guarantee the
   * shared type's own doc promises.
   */
  private crossChannelBlockMessage(info: CoachCrossChannelBlockInfo) {
    switch (info.kind) {
      case 'app': return LL.coach.tier.crossChannel.appBlock();
      case 'store': return LL.coach.tier.crossChannel.webBlock({ store: info.storeName });
      case 'legacyWeb': return LL.coach.tier.crossChannel.webBlockPayPro();
      case 'generic': return LL.coach.tier.crossProviderConflict();
    }
  }

  /**
   * The provider of the user's *active personal* membership — the web
   * (PayPro) rail or one of the two app stores — or null when they have
   * none. We read the reconciled `licenseType`: for a user who is not yet
   * a coach it reflects their personal subscription. Non-subscription
   * license types (key / coach grant / none) return null — there is no
   * paid personal membership whose channel a coach purchase could clash
   * with. Gated on Paid so an expired/free user (no live membership) is
   * treated as "none."
   */
  private activePersonalProvider(): SubscriptionBillingProvider | null {
    // A PAUSED Paddle sub is checked BEFORE the role gate, because a paused sub
    // grants no access and so drops the role to Unknown. Without this the
    // become-a-coach flow would open the store tier picker, the user would
    // actually be CHARGED at Apple/Google, and only then would the server's
    // cross-provider gate reject it — a charge with no entitlement. The server
    // side of this same gate (livePersonalBillingProvider) counts a paused
    // block as live for exactly this reason; the two must agree. (#4189)
    if (isPausedPaddleSubscriber(this.auth.couch)) {
      return 'paddle';
    }
    // not Paid → no live personal membership whose channel could clash
    if (this.auth.getAuthRole() < AuthRole.Paid) return null;
    // pass coachTierData.provider so an active coach (licenseType CoachTier)
    // resolves to their store rather than null (V6 reverse-direction mapping).
    return licenseTypeToBillingProvider(this.auth.couch?.licenseType, this.auth.couch?.coachTierData?.provider);
  }

  /**
   * The billing channel a NEW coach plan would use on this platform: the
   * app store on native (Google Play on Android, Apple on iOS), the web
   * (Paddle per-seat) rail otherwise. Web coaching bills on Paddle (Phase 4,
   * #3919), NOT the retired PPG metered rail — so a Paddle-personal web coach
   * resolves to the same channel and the same-channel gate lets them through,
   * while a PayPro-personal web user is (correctly) blocked as cross-provider.
   */
  private currentCoachChannel(): SubscriptionBillingProvider {
    if (!isCapacitorNative()) return 'paddle';
    return getCapacitorPlatform() === 'ios' ? 'apple' : 'googlePlay';
  }

  // store platforms: load the tier catalog + live prices and present a
  // picker; on selection, run the coach-tier purchase
  private async presentCoachTierPickerAsync(): Promise<void> {
    const loading = this.loadingCtrl.create({ content: LL.common.loading.pleaseWait() });
    void loading.present();

    // A coach whose earlier purchase was charged but never recorded comes back
    // HERE, because Become a coach is the only entry point they have. Finish that
    // purchase before offering to sell them another one; it never throws, and
    // does nothing at all for the coach who has no unfinished purchase.
    if (await this.coachTierService.retryUnconfirmedPurchaseIfAnyAsync()) {
      // it just granted the plan they had already paid for, so there is nothing
      // left to sell them — showing the picker now would offer a second one.
      void loading.dismiss();
      await this.alertCtrl.showAlertAsync({
        title: LL.coach.tier.becomeCoach(),
        message: LL.coach.tier.purchaseSuccess(),
        buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: null }],
      });
      return;
    }

    let tiers: CoachTierOption[];
    try {
      tiers = await this.coachTierService.loadTiersAsync();
    } catch (error) {
      void loading.dismiss();
      this.logging.trackAbnormalException(error, LL.common.errors.generic());
      return;
    }
    void loading.dismiss();

    // only tiers whose store product resolved (has a price) are buyable
    const buyable = tiers.filter(t => t.product !== null);
    if (buyable.length === 0) {
      await this.alertCtrl.showAlertAsync({
        title: LL.coach.tier.becomeCoach(),
        message: LL.coach.tier.noTiersAvailable(),
        buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: null }],
      });
      return;
    }

    const opts: PopupMenuOptions = { buttons: [] };
    for (const tier of buyable) {
      opts.buttons.push({
        text: LL.coach.tier.tierOption({ count: tier.entry.cap, price: tier.product!.priceText }),
        icon: 'people',
        handler: () => {
          void this.purchaseCoachTierAsync(tier);
        },
      });
    }
    void this.popupCtrl.createMenu(opts);
  }

  private async purchaseCoachTierAsync(tier: CoachTierOption): Promise<void> {
    const loading = this.loadingCtrl.create({ content: LL.common.loading.pleaseWait() });
    void loading.present();
    const result = await this.coachTierService.purchaseTierAsync(tier);
    void loading.dismiss();

    switch (decideCoachTierOutcomeMessage(result)) {
      case CoachTierOutcomeMessage.None:
        // the coach backed out, so there is nothing to say
        return;
      case CoachTierOutcomeMessage.AlreadyLinked:
        // the store subscription belongs to a different app account — tell
        // the coach which one
        await showCoachAlreadyLinkedAlertAsync(this.alertCtrl, result.linkedEmail);
        return;
      case CoachTierOutcomeMessage.OtherAccount:
        // the store says a different one of our accounts started this purchase,
        // and no account we can name holds it (#5384)
        await showOtherAccountAlertAsync(this.alertCtrl);
        return;
      case CoachTierOutcomeMessage.StoreRefused:
        // the store refuses this token every time, so "try again" would be
        // advice that cannot work (#6243)
        this.logging.trackSilentProblem('CoachTierPurchaseStoreRefused', { error: result.error });
        await showStoreRefusedAlertAsync(this.alertCtrl);
        return;
      case CoachTierOutcomeMessage.AlreadyOwned:
        // they already hold this tier — point them at Change Plan instead of
        // showing a generic failure
        await this.alertCtrl.showAlertAsync({
          title: LL.coach.tier.becomeCoach(),
          message: LL.coach.tier.alreadyOwned(),
          buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: null }],
        });
        return;
      case CoachTierOutcomeMessage.CrossProviderConflict:
        // the server refused because a live personal membership on ANOTHER
        // payment channel would be double-billed by a coach plan here (the
        // single-provider policy). Tell them to cancel that one first rather
        // than showing a generic failure. See
        // design-docs/coach-subscription-coordination.md §6.
        await this.alertCtrl.showAlertAsync({
          title: LL.coach.tier.becomeCoach(),
          message: LL.coach.tier.crossProviderConflict(),
          buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: null }],
        });
        return;
      case CoachTierOutcomeMessage.ConfirmationPending:
        // the store took the money and our confirmation never got an answer, so we
        // do not know that the purchase failed and must not say it did. Since
        // #5053 the message says the plan MAY finish activating at the next app
        // open: the server parks a claim on its own store_unavailable branch, and
        // when our call threw instead, the phone kept the purchase and re-sends it
        // at the next app open. It still says "may" rather than "will" on purpose —
        // read the caveat on CoachTierActionResult.confirmationFailedAfterPurchase
        // before changing this wording, because both of those recoveries are
        // best-effort. See design-docs/iap-subscriptions.md, "The same rule,
        // everywhere else".
        this.logging.trackSilentProblem('CoachTierPurchaseNotConfirmed', { error: result.error });
        await this.alertCtrl.showAlertAsync({
          title: LL.coach.tier.becomeCoach(),
          message: LL.coach.tier.purchaseNotYetConfirmed(),
          buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: null }],
        });
        return;
      case CoachTierOutcomeMessage.Failed:
        this.logging.trackAbnormalError(result.error ?? 'unknown', LL.coach.tier.purchaseFailed());
        return;
      case CoachTierOutcomeMessage.Succeeded:
        await this.alertCtrl.showAlertAsync({
          title: LL.coach.tier.becomeCoach(),
          message: LL.coach.tier.purchaseSuccess(),
          buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: null }],
        });
        return;
    }
  }
}
